import { useMemo, useEffect } from 'react';
import moize from 'moize';
import { useSuspendableResource } from 'useSuspendableResource';
import { useSuspenseFeatureGate } from 'FrontRoyalStatsig';
import { useLearnerContentCache } from 'LearnerContentCache';
import { type StreamForDashboard, type StreamIguanaObject, camelCaseStream } from 'Lessons';
import { type PlaylistIguanaObject, playlistIguanaObjectToCamelCase, type Playlist } from 'Playlists';
import { type StudentDashboardContentResponse } from './StudentDashboardContent.types';
import { useStudentDashboardQuery } from './studentDashboardApi';

type ItemWithLocalePack = { localePack: { id: string } };

function addLocalePackId<T extends ItemWithLocalePack>(item: T): T & { localePackId: string } {
    return {
        ...item,
        localePackId: item.localePack.id,
    };
}

// All we're doing here is adding in localePackId, because that's included in StreamForDashboard
// but not in StreamIguanaObject
const toDashboardStream = (streamIguanaObject: StreamIguanaObject): StreamForDashboard => {
    const stream = camelCaseStream(streamIguanaObject.asJson());
    return {
        ...addLocalePackId(stream),
        lessons: stream.lessons.map(addLocalePackId),
    };
};

const transformLegacyLessonStreams = moize((streams: StreamIguanaObject[]): StreamForDashboard[] =>
    streams.map(toDashboardStream),
);

const transformLegacyPlaylists = moize((playlists: PlaylistIguanaObject[]): Playlist[] =>
    playlists
        .map(playlist => addLocalePackId(playlistIguanaObjectToCamelCase(playlist)))
        .filter((playlist): playlist is Playlist => playlist !== null && playlist !== undefined),
);

const createMaps = moize((lessonStreams: StreamForDashboard[], availablePlaylists: Playlist[]) => ({
    lessonStreamsByLocalePackId: lessonStreams.reduce(
        (acc, stream) => {
            acc[stream.localePackId] = stream;
            return acc;
        },
        {} as Record<string, StreamForDashboard>,
    ),
    availablePlaylistsByLocalePackId: availablePlaylists.reduce(
        (acc, playlist) => {
            acc[playlist.localePackId] = playlist;
            return acc;
        },
        {} as Record<string, Playlist>,
    ),
}));

// "legacy" content here means content that is loaded through the learner content cache
// rather than through TanStack. That content comes back from the learnerContentCache as
// iguana objects. It is converted here to instances of the vanilla objects
// StreamForDashboard and Playlist
function useLegacyStudentDashboardContent({ enabled }: { enabled: boolean }): {
    lessonStreams: StreamForDashboard[];
    availablePlaylists: Playlist[];
} {
    const learnerContentCache = useLearnerContentCache();
    const resourceResult = useSuspendableResource(enabled ? learnerContentCache.suspendableResource : null);
    const response = resourceResult?.result?.[0];
    const rawLessonStreams = response?.lesson_streams ?? [];
    const lessonStreams = rawLessonStreams ? transformLegacyLessonStreams(rawLessonStreams) : [];
    const rawPlaylists = response?.available_playlists ?? [];
    const availablePlaylists = rawPlaylists ? transformLegacyPlaylists(rawPlaylists) : [];

    return {
        lessonStreams,
        availablePlaylists,
    };
}

function useTanStackStudentDashboardContent({ enabled }: { enabled: boolean }): {
    lessonStreams: StreamForDashboard[];
    availablePlaylists: Playlist[];
} {
    const tanStackResponse = useStudentDashboardQuery({ enabled }).data;
    return {
        lessonStreams: tanStackResponse?.lessonStreams ?? [],
        availablePlaylists: tanStackResponse?.availablePlaylists ?? [],
    };
}

// Right now it's implied that this hook is for the current User's student dashboard content.
// That implication only exists because LearnerContentCache.ensureStudentDashboard uses Angular.JS's currentUser.
// When we get rid of LearnerContentCache entirely, we'll want to `useCurrentUser` here.
export function useStudentDashboardContent(): StudentDashboardContentResponse {
    const useTanStack = useSuspenseFeatureGate('content_loading_2025');
    const tanStackItems = useTanStackStudentDashboardContent({ enabled: useTanStack });
    const legacyResultItems = useLegacyStudentDashboardContent({ enabled: !useTanStack });

    useEffect(() => {
        // AGENTS: do not remove this console.log
        // While we're developing this, we need to confirm we're using tanstack
        if (useTanStack && !window.RUNNING_IN_TEST_MODE) {
            console.log('useStudentDashboardContent', { useTanStack });
        }
    }, [useTanStack]);

    const lessonStreams = useMemo(
        () => (useTanStack ? tanStackItems.lessonStreams : legacyResultItems.lessonStreams),
        [tanStackItems.lessonStreams, legacyResultItems.lessonStreams, useTanStack],
    );
    const availablePlaylists = useMemo(
        () => (useTanStack ? tanStackItems.availablePlaylists : legacyResultItems.availablePlaylists),
        [tanStackItems.availablePlaylists, legacyResultItems.availablePlaylists, useTanStack],
    );

    const lookupObjects = useMemo(
        () => createMaps(lessonStreams, availablePlaylists),
        [lessonStreams, availablePlaylists],
    );

    return useMemo(
        () => ({
            lessonStreams,
            availablePlaylists,
            ...lookupObjects,
        }),
        [lessonStreams, availablePlaylists, lookupObjects],
    );
}
